import type { Atom, WritableAtom } from 'jotai';
import { atom, Getter, Setter } from 'jotai';
import { atomFamily, atomWithReset, RESET } from 'jotai/utils';

import { getAuth } from '@/hooks/useAuth';

import { DataViewAtomFamilyKey, dataViewSortDefaults } from './constants';
import { DataViewKey, SortState } from './types';

export type ResetAtom<T> = ReturnType<typeof atomWithReset<T>>;

export interface FiltersState<T extends Record<string, unknown>> {
  in: Partial<Record<keyof T, string[]>>;
  search: string;
  searchKey?: keyof T | (keyof T)[];
}

export interface DisplayState<T extends Record<string, unknown>> {
  allItems: T[];
  items: T[];
  total: number;
}

const _sourceDataFamily = atomFamily<DataViewKey, ResetAtom<unknown[]>>(() =>
  atomWithReset<unknown[]>([]),
);

export const sourceDataFamily = <T extends object>(
  key: DataViewKey,
): ResetAtom<T[]> => _sourceDataFamily(key) as ResetAtom<T[]>;

const _filtersFamily = atomFamily<
  DataViewKey,
  ResetAtom<FiltersState<Record<string, unknown>>>
>(() =>
  atomWithReset<FiltersState<Record<string, unknown>>>({
    in: {} as Record<string, string[]>,
    search: '',
    searchKey: undefined,
  }),
);

const _sortFamily = atomFamily<
  DataViewKey,
  ResetAtom<SortState<Record<string, unknown>> | null>
>(key =>
  atomWithReset<SortState<Record<string, unknown>> | null>(
    dataViewSortDefaults[key] ?? {
      key: undefined,
      direction: 'asc',
    },
  ),
);

/** -------- TYPED ACCESSORS (reuse same underlying singletons) -------- */
export const filtersFamilyFor = <T extends Record<string, unknown>>() =>
  _filtersFamily as unknown as (p: DataViewKey) => ResetAtom<FiltersState<T>>;

// @TODO: Tighten this along with type narrow in the downstream onChange handlers.
// For now we are leaving Any for ergonomics.
export const sortFamilyFor = <T extends Record<string, unknown>>() =>
  _sortFamily as unknown as (p: DataViewKey) => ResetAtom<SortState<T> | null>;

const getLastOptedInAt = (item: Record<string, unknown>) =>
  (
    item.readonlySubscriptionSettings as
      | { lastOptedInAt?: unknown }
      | null
      | undefined
  )?.lastOptedInAt;

const toMs = (v: unknown) => {
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    if (!isNaN(ms)) return ms;
  }
  return null;
};

// Singleton at module scope
const _displayFamily = atomFamily<
  DataViewKey,
  Atom<DisplayState<Record<string, unknown>>>
>(p =>
  atom<DisplayState<Record<string, unknown>>>(get => {
    const src: Record<string, unknown>[] = get(sourceDataFamily(p));
    const filt = get(_filtersFamily(p));
    const sort = get(_sortFamily(p));
    const isLicensee = getAuth()?.isLicensee ?? false;
    const country = getAuth()?.country.id ?? '';

    const filtEntries = Object.entries(filt.in);
    const includeCompleted = filtEntries.some(
      x => x[0] === 'isCompleted' && x[1]?.includes('include_completed'),
    );
    // Pre-filtering the items to make sure that "completed" items are not included by default
    let items = [...src].filter(it =>
      includeCompleted ? true : it.isCompleted !== 'include_completed',
    );

    // https://cfacorp.atlassian.net/browse/PATHWAY-3622 remove when the BE filters out readonly plans for licensee users
    if (isLicensee) {
      items = items.filter(
        it =>
          !it.readonly &&
          it.planType !== 'COMPLIANCE' &&
          it.planType !== 'ANCHOR',
      );
    }
    // https://cfacorp.atlassian.net/browse/PATHWAY-3661 remove when the BE filters out food safety plans for CA and PR users
    if (country === 'CA' || country === 'PR') {
      const foodSafetyIds = [
        'crn:pathway:plan:01KH20ZHAPPPX6S35X0TGCYP4X',
        'crn:pathway:plan:01KTKRSVWVDG3YSV9Q7G0G93WC',
        'crn:pathway:plan:01KHP5W6MA9FHW9G6G8ECHQH0K',
        'crn:pathway:plan:01KKCF2QJB025CY9W93QS5NXMF',
      ];
      items = items.filter(it => !foodSafetyIds.includes(String(it.id)));
    }

    // in[] filters
    for (const [k, list] of filtEntries) {
      const listToLowerCase =
        // Disect this later when we're more confident with types
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
        list?.map(i => String(i).toLowerCase()) ?? undefined;

      if (Array.isArray(list) && list.length) {
        items = items.filter(it => {
          const raw = it[k];
          const vals = Array.isArray(raw) ? raw : [raw];
          //  Completed plans are filtered earlier and want to keep the tag, but rely on the previous filtering
          return k === 'isCompleted' && includeCompleted
            ? true
            : vals.some(v =>
                listToLowerCase?.includes(String(v ?? '').toLowerCase()),
              );
        });
      }
    }

    // search
    if (filt.search.trim() && filt.searchKey) {
      const q = filt.search.toLowerCase();
      const sks = Array.isArray(filt.searchKey)
        ? filt.searchKey
        : [filt.searchKey];

      items = items.filter(it =>
        sks.some(sk => {
          const raw = it[sk];
          const vals = Array.isArray(raw) ? raw : [raw];
          return vals.some(v =>
            String(v ?? '')
              .toLowerCase()
              .includes(q),
          );
        }),
      );
    }

    // sort
    if (sort?.key) {
      const dir = sort.direction === 'asc' ? 1 : -1;
      const ob = sort.key;
      items = [...items].sort((a, b) => {
        const isCreatedSort = ob === 'createdDateTime' || ob === 'created';
        const av = isCreatedSort
          ? (toMs(getLastOptedInAt(a)) ?? toMs(a[ob]) ?? a[ob])
          : a[ob];
        const bv = isCreatedSort
          ? (toMs(getLastOptedInAt(b)) ?? toMs(b[ob]) ?? b[ob])
          : b[ob];

        if (sort.key === 'dueDate') {
          // Second priority: items with completionDate AND status === 'COMPLETED' go to the very end
          const aIsCompleted =
            a.completionDate != null && a.status === 'COMPLETED';
          const bIsCompleted =
            b.completionDate != null && b.status === 'COMPLETED';

          if (aIsCompleted && bIsCompleted) return 0;
          if (aIsCompleted) return 1;
          if (bIsCompleted) return -1;

          // Push items without dueDate to end (but before completed items)
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
        } else {
          // Handle null values with respect to sort direction
          if (av == null && bv == null) return 0;
          if (av == null) return -1 * dir;
          if (bv == null) return 1 * dir;
        }

        if (typeof av === 'number' && typeof bv === 'number')
          return (av - bv) * dir;
        return JSON.stringify(av).localeCompare(JSON.stringify(bv)) * dir;
      });
    }

    const total = items.length;

    return {
      allItems: items,
      items,
      total,
    };
  }),
);

//Typed accessor that reuses the singleton.
export const displayDataFamilyFor = <T extends Record<string, unknown>>() =>
  _displayFamily as unknown as (p: DataViewKey) => Atom<DisplayState<T>>;

/**
 * Write-only reset for a single data-view key. Consumers get it through
 * `useSetAtom`, so they can reset filters/sort without subscribing to them.
 */
export const resetDataViewFamily = atomFamily<
  DataViewKey,
  WritableAtom<null, [], void>
>(key =>
  atom(null, (_get: Getter, set: Setter) => {
    set(_filtersFamily(key), RESET);
    set(_sortFamily(key), RESET);
  }),
);

/**
 * All possible data-view keys from constants.
 */
const allDataViewKeys = Object.values(DataViewAtomFamilyKey) as DataViewKey[];

/**
 * Resets all data-view atoms (source, filters, sort) for all possible keys.
 * Call this from the layout's unmount cleanup.
 *
 * Note: This resets all keys from constants, even if they haven't been accessed yet.
 * Resetting an atom that hasn't been created is safe (it will be created and reset).
 */
export const resetAllDataViewAtom = atom(null, (_get: Getter, set: Setter) => {
  for (const key of allDataViewKeys) {
    set(_sourceDataFamily(key), RESET);
    set(_filtersFamily(key), RESET);
    set(_sortFamily(key), RESET);
  }
});
